import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { fmtDate } from "@/components/ui-kit";
import { toast } from "sonner";
import { Check, X } from "lucide-react";

const TONE = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", rejected: "bg-rose-100 text-rose-700" };

export function StaffLeavePanel() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const load = () => api.get("/leave").then((r) => setRows(r.data.filter((x) => x.kind === "staff"))).catch(() => {});
  useEffect(() => { load(); }, []);
  async function decide(lv, status) {
    try { await api.patch(`/leave/${lv.id}`, { status }); toast.success(status === "approved" ? "Approved — attendance marked as leave" : "Rejected"); load(); } catch (e) { toast.error(formatApiError(e)); }
  }
  const shown = rows.filter((x) => filter === "all" || x.status === filter);
  return (
    <div className="bg-white rounded-2xl shadow-sm p-5" data-testid="staff-leave-panel">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h3 className="font-display font-bold text-slate-900 flex-1">Staff leave requests</h3>
        {["pending", "approved", "rejected", "all"].map((k) => (
          <button key={k} data-testid={`leave-filter-${k}`} onClick={() => setFilter(k)} className={`text-xs font-semibold px-3 py-1.5 rounded-full capitalize transition-colors ${filter === k ? "bg-violet-600 text-white" : "bg-violet-50 text-violet-700 hover:bg-violet-100"}`}>{k}</button>
        ))}
      </div>
      <p className="text-xs text-slate-500 mb-3">Staff send requests from their Leave page. Approved days are marked "Leave" in staff attendance and are not counted as absent in payroll.</p>
      {shown.length === 0 ? <div className="text-sm text-slate-500 py-3">No {filter === "all" ? "" : filter} requests.</div> : shown.map((lv) => (
        <div key={lv.id} data-testid={`staff-leave-${lv.id}`} className="flex flex-wrap items-center gap-3 py-3 border-t border-slate-100 text-sm">
          <div className="flex-1 min-w-[200px]">
            <div className="font-semibold">{lv.person_name} <span className="text-xs text-slate-400">{ROLE_LABELS[lv.requested_by_role] || lv.requested_by_role}</span></div>
            <div className="text-slate-600">{fmtDate(lv.from_date)} – {fmtDate(lv.to_date)} • {lv.reason}</div>
            {lv.decided_by && <div className="text-xs text-slate-400">by {lv.decided_by}</div>}
          </div>
          <Badge className={TONE[lv.status]}>{lv.status}</Badge>
          {lv.status === "pending" && lv.requested_by !== user?.id && (
            <span className="flex gap-2">
              <Button data-testid={`leave-approve-${lv.id}`} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => decide(lv, "approved")}><Check size={14} className="mr-1" /> Approve</Button>
              <Button data-testid={`leave-reject-${lv.id}`} size="sm" variant="outline" className="text-rose-600" onClick={() => decide(lv, "rejected")}><X size={14} className="mr-1" /> Reject</Button>
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

export function StaffRequestsPanel({ isAdmin, refresh, onApproved }) {
  const { user } = useAuth();
  const head = user?.role === "center_head";
  const canDecide = (x) => isAdmin || (head && x.requested_by !== user?.id);
  const [rows, setRows] = useState([]);
  const load = () => api.get("/staff-mgmt/requests").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [refresh]);
  async function decide(x, action) {
    const note = action === "reject" ? window.prompt("Reason for rejecting (optional)") ?? null : "";
    if (note === null) return;
    try { await api.post(`/staff-mgmt/requests/${x.id}/${action}`, { note }); toast.success(action === "approve" ? `${x.data.name} added` : "Request rejected"); load(); if (action === "approve") onApproved(); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <div className="bg-white rounded-2xl shadow-sm p-5" data-testid="staff-requests-panel">
      <h3 className="font-display font-bold text-slate-900 mb-1">{isAdmin ? "Staff requests from Principals" : head ? "Staff requests in your branch" : "My staff requests"}</h3>
      <p className="text-xs text-slate-500 mb-3">{isAdmin || head ? "Principals can't add staff directly. Approve to create the login, or reject with a reason." : "Your Center Head or Admin reviews every new staff request."}</p>
      {rows.length === 0 ? <div className="text-sm text-slate-500 py-2">No requests yet.</div> : rows.map((x) => (
        <div key={x.id} data-testid={`staff-request-${x.id}`} className="flex flex-wrap items-center gap-3 py-3 border-t border-slate-100 text-sm">
          <div className="flex-1 min-w-[220px]">
            <div className="font-semibold">{x.data.name} <span className="text-xs text-slate-400">{ROLE_LABELS[x.data.role]} • {x.data.designation || "—"}</span></div>
            <div className="text-slate-600">{x.email} • requested by {x.requested_by_name} on {fmtDate(x.created_at)}</div>
            {x.decided_by && <div className="text-xs text-slate-400">{x.status} by {x.decided_by}{x.note ? ` — ${x.note}` : ""}</div>}
          </div>
          <Badge className={TONE[x.status]}>{x.status}</Badge>
          {canDecide(x) && x.status === "pending" && (
            <span className="flex gap-2">
              <Button data-testid={`staff-request-approve-${x.id}`} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => decide(x, "approve")}><Check size={14} className="mr-1" /> Approve</Button>
              <Button data-testid={`staff-request-reject-${x.id}`} size="sm" variant="outline" className="text-rose-600" onClick={() => decide(x, "reject")}><X size={14} className="mr-1" /> Reject</Button>
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

export function StaffRolesPanel({ staff, onOpen }) {
  const [roles, setRoles] = useState([]);
  useEffect(() => { api.get("/staff-mgmt/roles").then((r) => setRoles(r.data)).catch(() => {}); }, [staff.length]);
  return (
    <div className="space-y-4" data-testid="staff-roles-panel">
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {roles.map((r) => (
          <div key={r.role} data-testid={`role-card-${r.role}`} className="bg-white rounded-2xl shadow-sm p-4">
            <div className="text-xs text-slate-500">{ROLE_LABELS[r.role]}</div>
            <div className="font-display text-2xl font-extrabold text-violet-800">{r.count}</div>
            <div className="text-[11px] text-slate-400">{r.assignable ? "You can assign this role" : "Admin only"}</div>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-2xl shadow-sm p-5">
        <h3 className="font-display font-bold text-slate-900 mb-2">Who has which role</h3>
        <p className="text-xs text-slate-500 mb-3">Open a staff member and use Edit to change their app role. The role decides which screens they can use.</p>
        {roles.map((r) => {
          const people = staff.filter((s) => s.role === r.role);
          if (!people.length) return null;
          return (
            <div key={r.role} className="py-2 border-t border-slate-100">
              <div className="text-xs font-bold text-violet-700 uppercase mb-1">{ROLE_LABELS[r.role]}</div>
              <div className="flex flex-wrap gap-2">{people.map((p) => <button key={p.id} data-testid={`role-person-${p.id}`} onClick={() => onOpen(p.id)} className="text-sm px-3 py-1 rounded-full bg-slate-100 hover:bg-violet-100 transition-colors">{p.name}</button>)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
